/**
 * ConnectionsPage Component
 *
 * Layout for the /connections route. Owns the page chrome -- SEO metadata,
 * heading, and the tab bar -- and renders whichever tab matched through
 * <Outlet />.
 *
 * Holds no state: each tab is a nested route, so the URL is the tab state
 * and NavLink supplies the active-tab class.
 *
 * Does not publish chat-widget context. MentorDirectory and FindPartners
 * each call useChatPageEffect themselves, and only the matched one is
 * mounted.
 */
import React from 'react';
import { Outlet, NavLink, Navigate, useMatch } from 'react-router-dom';
import { Container, Nav } from 'react-bootstrap';

import SEO from '@/components/common/SEO';
import useCapability from '@/shared/hooks/useCapability';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import './Connections.css';

// `key` doubles as the relative route path and the React list key.
// `capability` is the per-tenant flag that decides whether the tab exists.
// `title` and `describe` supply this tab's SEO title and description.
// `lead` is the page copy used when this is the tenant's only tab.
const TABS = [
    {
        key: 'mentors',
        label: 'Mentors',
        icon: '🧑‍🏫',
        capability: 'mentoring',
        title: 'Find Mentors',
        lead: 'Find mentors who share your interests and goals.',
        describe: (name) =>
            `Connect with mentors in ${name} who share your interests and goals.`,
    },
    {
        key: 'partners',
        label: 'Partners',
        icon: '🤝',
        capability: 'discoverPartners',
        title: 'Find Partners',
        lead: 'Discover cross-sector partners to strengthen your network.',
        describe: (name) =>
            `Discover organizations in ${name} looking to collaborate across sectors and build new connections.`,
    },
];

// Page copy used when the tenant has both tabs.
const BOTH_TABS_LEAD =
    'Find mentors who share your interests and cross-sector partners to strengthen your network.';

export default function ConnectionsPage() {
    // Hooks run unconditionally and before any branching.
    const mentoring = useCapability('mentoring');
    const partners = useCapability('discoverPartners');
    const { ecosystem } = useEcosystem();
    // The trailing `/*` matches the tab path with or without a trailing
    // slash.
    const onPartnersRoute = useMatch('/connections/partners/*');
    const onMentorsRoute = useMatch('/connections/mentors/*');

    const ecosystemName = ecosystem?.name || 'RILE Connect';
    // Maps each capability name to its flag, keyed to match `tab.capability`.
    const flags = { mentoring, discoverPartners: partners };
    const tabs = TABS.filter((tab) => flags[tab.capability]);

    // `activeTab` is the matched tab when the tenant has it enabled, and
    // otherwise the tenant's first enabled tab.
    let routeKey = null;
    if (onPartnersRoute) routeKey = 'partners';
    else if (onMentorsRoute) routeKey = 'mentors';
    const activeTab = tabs.find((tab) => tab.key === routeKey) || tabs[0];

    const breadcrumbs = [
        { name: 'Home', path: '/' },
        { name: 'Connections', path: '/connections' },
    ];
    // Appends the active tab as a third crumb, so each tab has its own leaf.
    if (activeTab) {
        breadcrumbs.push({
            name: activeTab.label,
            path: `/connections/${activeTab.key}`,
        });
    }

    return (
        <>
            <SEO
                title={activeTab ? activeTab.title : 'Connections'}
                description={
                    activeTab
                        ? activeTab.describe(ecosystemName)
                        : `Explore connections across ${ecosystemName}.`
                }
                breadcrumbs={breadcrumbs}
            />
            <Container className="py-5">
                <h1 className="text-primary mb-2">Connections</h1>
                <p className="lead mb-4">
                    {tabs.length === 1 ? tabs[0].lead : BOTH_TABS_LEAD}
                </p>

                {/* Rendered only when the tenant has both tabs. */}
                {tabs.length > 1 && (
                    <Nav variant="tabs" className="mb-4">
                        {tabs.map((tab) => (
                            <Nav.Item key={tab.key}>
                                <Nav.Link
                                    as={NavLink}
                                    to={tab.key}
                                    className="d-flex align-items-center gap-2"
                                >
                                    <span aria-hidden="true">{tab.icon}</span>
                                    <span className="d-none d-sm-inline">
                                        {tab.label}
                                    </span>
                                    {/* Screen-reader label below `sm`, where
                                        the visible one is hidden. */}
                                    <span className="visually-hidden d-sm-none">
                                        {tab.label}
                                    </span>
                                </Nav.Link>
                            </Nav.Item>
                        ))}
                    </Nav>
                )}

                <Outlet />
            </Container>
        </>
    );
}

/**
 * Index element for /connections: sends the visitor to the first tab their
 * tenant actually has.
 *
 * This is the only place the two capabilities are considered together; each
 * tab route is guarded by its own single-name CapabilityRoute. The NavBar
 * entry for Connections applies the same either-or rule.
 *
 * Targets are relative, so they resolve against /connections and stay
 * correct under a tenant path prefix. `replace` keeps the redirect out of
 * history.
 */
export function ConnectionsIndexRedirect() {
    const mentoring = useCapability('mentoring');
    const partners = useCapability('discoverPartners');

    if (mentoring) return <Navigate to="mentors" replace />;
    if (partners) return <Navigate to="partners" replace />;
    // Neither capability: match CapabilityRoute's redirect-home behavior.
    return <Navigate to="/" replace />;
}
